import type { SupabaseClient } from "@supabase/supabase-js";

/** Outcome of a server-gated display-name claim (Foundations F2). */
export type SaveNameResult = "ok" | "taken" | "rejected" | "error";

/** One finished run's durable stats (Foundations F2 — UNRANKED, owner-private). */
export type RunStatInput = {
  wavesCompleted: number;
  kills: number;
  deaths: number;
  score: number;
  aiMode: string | null;
};

/** One row of Firestrike's unranked best-runs board (Games G2, DR-0074):
 *  an account's best run, ranked by score. */
export type BestRun = {
  rank: number;
  displayName: string;
  score: number;
  wavesCompleted: number;
  kills: number;
  deaths: number;
  aiMode: string | null;
  playedAt: string;
  /** The caller's own row. */
  isYou: boolean;
};

/**
 * Which runs a ranked board counts (Games G3, DR-0086). Every filter is optional and
 * unset means no filter: the hub's Basecamp board passes only the event and its rules
 * version, so whatever the server recorded for them counts.
 */
export type RankedBoardFilter = {
  /** An event's id (FIRESTRIKE_EVENTS, e.g. "basecamp"). */
  event?: string;
  rulesVersion?: number;
  /** ISO times: runs that finished at or after `from`, and before `to`. */
  from?: string;
  to?: string;
  runMode?: string;
  aiMode?: string;
  /** Rows to return (the server allows 1 to 200; 50 when unset). */
  limit?: number;
};

/** One row of a ranked solo board: an account's best server-hosted solo run. */
export type RankedSoloRow = {
  rank: number;
  displayName: string;
  wavesCompleted: number;
  teamScore: number;
  kills: number;
  deaths: number;
  headshots: number;
  durationMs: number;
  runMode: string;
  aiMode: string;
  outcome: string;
  finishedAt: string;
  /** The caller's own row. */
  isYou: boolean;
};

/** One player in a ranked co-op run. */
export type RankedCoopPlayer = {
  displayName: string;
  kills: number;
  deaths: number;
  headshots: number;
  score: number;
  isYou: boolean;
};

/** One row of a ranked co-op board: a team's server-hosted run. */
export type RankedCoopRow = {
  rank: number;
  runId: number;
  /** Best score first. */
  players: RankedCoopPlayer[];
  wavesCompleted: number;
  teamScore: number;
  durationMs: number;
  runMode: string;
  aiMode: string;
  outcome: string;
  finishedAt: string;
  /** The caller was in this run. */
  hasYou: boolean;
};

type Json = Record<string, unknown>;

/** A board filter as the database functions' arguments: only the filters that are set. */
function rankedBoardArgs(filter: RankedBoardFilter): Json {
  const args: Json = {};
  if (filter.event !== undefined) args.p_event = filter.event;
  if (filter.rulesVersion !== undefined) args.p_rules_version = filter.rulesVersion;
  if (filter.from !== undefined) args.p_from = filter.from;
  if (filter.to !== undefined) args.p_to = filter.to;
  if (filter.runMode !== undefined) args.p_run_mode = filter.runMode;
  if (filter.aiMode !== undefined) args.p_ai_mode = filter.aiMode;
  if (filter.limit !== undefined) args.p_limit = filter.limit;
  return args;
}

/**
 * RLS-scoped persistence for the signed-in account (Foundations F2). Every row
 * is implicitly the caller's own — Postgres RLS confines reads/writes to
 * `auth.uid()`, so this never trusts a client-supplied id for ownership. The
 * server (DB + RLS) is the source of truth; localStorage is the cache.
 *
 * Constructed only once a Supabase session exists; the offline path (no session
 * / unconfigured) is the caller's concern (it simply skips the cloud layer).
 */
export class AccountStore {
  constructor(
    private readonly db: SupabaseClient,
    private readonly userId: string,
  ) {}

  // ---- settings (private, owner-only) ----

  async fetchSettings(): Promise<Json | null> {
    const { data, error } = await this.db.from("settings").select("data").eq("account_id", this.userId).maybeSingle();
    if (error || !data) return null;
    const blob = (data as { data?: unknown }).data;
    return blob && typeof blob === "object" ? (blob as Json) : null;
  }

  async saveSettings(data: Json): Promise<boolean> {
    const { error } = await this.db.from("settings").upsert({ account_id: this.userId, data }, { onConflict: "account_id" });
    return !error;
  }

  // ---- loadout (private, owner-only) ----

  async fetchLoadout(): Promise<Json | null> {
    const { data, error } = await this.db.from("loadouts").select("data").eq("account_id", this.userId).maybeSingle();
    if (error || !data) return null;
    const blob = (data as { data?: unknown }).data;
    return blob && typeof blob === "object" ? (blob as Json) : null;
  }

  async saveLoadout(data: Json): Promise<boolean> {
    const { error } = await this.db.from("loadouts").upsert({ account_id: this.userId, data }, { onConflict: "account_id" });
    return !error;
  }

  // ---- profile display name (public identity; the row is auto-created on sign-in) ----

  async fetchDisplayName(): Promise<string | null> {
    const { data, error } = await this.db.from("profiles").select("display_name").eq("account_id", this.userId).maybeSingle();
    if (error || !data) return null;
    const name = (data as { display_name?: unknown }).display_name;
    return typeof name === "string" && name.trim().length > 0 ? name : null;
  }

  /**
   * Claim/change the owned display name. The Postgres unique index + profanity
   * trigger are the authority; map their error codes to a typed result so the
   * UI can explain why. (`23505` = unique_violation → taken; `23514` =
   * check_violation from the gate trigger → rejected.)
   */
  async saveDisplayName(name: string): Promise<SaveNameResult> {
    const { error } = await this.db.from("profiles").update({ display_name: name }).eq("account_id", this.userId);
    if (!error) return "ok";
    if (error.code === "23505") return "taken";
    if (error.code === "23514") return "rejected";
    return "error";
  }

  // ---- entitlements (server-authoritative: owner READ-only; only the service
  //      role writes — today a DB seed, later the on-chain reader. See DR-0024) ----

  /** The account's granted item ids (RLS returns only this account's rows). */
  async fetchEntitlements(): Promise<string[]> {
    const { data, error } = await this.db.from("entitlements").select("item_id");
    if (error || !data) return [];
    return (data as Array<{ item_id?: unknown }>).map((r) => String(r.item_id ?? "")).filter((s) => s.length > 0);
  }

  // ---- run_stats (durable, UNRANKED, owner-insert) ----

  /** Persist one finished run's stats for this account. Returns success. */
  async insertRunStat(stat: RunStatInput): Promise<boolean> {
    const { error } = await this.db.from("run_stats").insert({
      account_id: this.userId,
      waves_completed: stat.wavesCompleted,
      kills: stat.kills,
      deaths: stat.deaths,
      score: stat.score,
      ai_mode: stat.aiMode,
    });
    return !error;
  }

  // ---- best runs (Games G2: public, UNRANKED; run_stats itself stays private) ----

  /** The board's top `limit` rows (each account's best run), or null when it
   *  can't be read (offline, or the database function isn't there). */
  async fetchBestRuns(limit = 20): Promise<BestRun[] | null> {
    const { data, error } = await this.db.rpc("best_runs", { p_limit: limit });
    if (error || !Array.isArray(data)) return null;
    // A row without a rank isn't the board's (a stand-in backend's echo, say).
    return (data as Array<Record<string, unknown>>).filter((r) => Number(r.rank) >= 1).map((r) => ({
      rank: Number(r.rank ?? 0),
      displayName: String(r.display_name ?? ""),
      score: Number(r.score ?? 0),
      wavesCompleted: Number(r.waves_completed ?? 0),
      kills: Number(r.kills ?? 0),
      deaths: Number(r.deaths ?? 0),
      aiMode: typeof r.ai_mode === "string" ? r.ai_mode : null,
      playedAt: String(r.played_at ?? ""),
      isYou: r.is_you === true,
    }));
  }

  // ---- ranked boards (Games G3: written only by the game server, public; DR-0086) ----

  /** A ranked solo board (each account's best run), or null when it can't be read. */
  async fetchRankedSoloBoard(filter: RankedBoardFilter = {}): Promise<RankedSoloRow[] | null> {
    const { data, error } = await this.db.rpc("ranked_solo_board", rankedBoardArgs(filter));
    if (error || !Array.isArray(data)) return null;
    return (data as Json[]).filter((r) => Number(r.rank) >= 1).map((r) => ({
      rank: Number(r.rank ?? 0),
      displayName: String(r.display_name ?? ""),
      wavesCompleted: Number(r.waves_completed ?? 0),
      teamScore: Number(r.team_score ?? 0),
      kills: Number(r.kills ?? 0),
      deaths: Number(r.deaths ?? 0),
      headshots: Number(r.headshots ?? 0),
      durationMs: Number(r.duration_ms ?? 0),
      runMode: String(r.run_mode ?? ""),
      aiMode: String(r.ai_mode ?? ""),
      outcome: String(r.outcome ?? ""),
      finishedAt: String(r.finished_at ?? ""),
      isYou: r.is_you === true,
    }));
  }

  /** A ranked co-op board (the best team runs), or null when it can't be read. */
  async fetchRankedCoopBoard(filter: RankedBoardFilter = {}): Promise<RankedCoopRow[] | null> {
    const { data, error } = await this.db.rpc("ranked_coop_board", rankedBoardArgs(filter));
    if (error || !Array.isArray(data)) return null;
    return (data as Json[]).filter((r) => Number(r.rank) >= 1).map((r) => ({
      rank: Number(r.rank ?? 0),
      runId: Number(r.run_id ?? 0),
      players: (Array.isArray(r.players) ? (r.players as Json[]) : []).map((p) => ({
        displayName: String(p.display_name ?? ""),
        kills: Number(p.kills ?? 0),
        deaths: Number(p.deaths ?? 0),
        headshots: Number(p.headshots ?? 0),
        score: Number(p.score ?? 0),
        isYou: p.is_you === true,
      })),
      wavesCompleted: Number(r.waves_completed ?? 0),
      teamScore: Number(r.team_score ?? 0),
      durationMs: Number(r.duration_ms ?? 0),
      runMode: String(r.run_mode ?? ""),
      aiMode: String(r.ai_mode ?? ""),
      outcome: String(r.outcome ?? ""),
      finishedAt: String(r.finished_at ?? ""),
      hasYou: r.has_you === true,
    }));
  }
}
